import { z } from 'zod';

import { localizedNameSchema } from './localized.js';
import { ratingSummarySchema } from './reviews.js';
import { WHEN_FILTERS } from './time-window.js';

const slug = z.string().regex(/^[a-z0-9-]{1,40}$/);

/** `bbox=west,south,east,north` in degrees (query string). */
export const bboxSchema = z
	.string()
	.transform((value) => value.split(',').map(Number))
	.pipe(
		z
			.tuple([
				z.number().min(-180).max(180),
				z.number().min(-90).max(90),
				z.number().min(-180).max(180),
				z.number().min(-90).max(90),
			])
			.refine(([west, south, east, north]) => west < east && south < north, {
				message: 'must be west,south,east,north with west < east and south < north',
			}),
	);

const filters = {
	city: slug,
	category: slug.optional(),
	when: z.enum(WHEN_FILTERS).optional(),
};

/** `GET /v1/events` (cards, cursor-paged by start time). */
export const eventsListQuerySchema = z.object({
	...filters,
	bbox: bboxSchema.optional(),
	/** Only events published after this moment (T-1334). */
	created_after: z.iso.datetime().optional(),
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const ME_EVENT_TABS = ['upcoming', 'created', 'past', 'saved'] as const;
export type MeEventTab = (typeof ME_EVENT_TABS)[number];

/** `GET /v1/me/events` (Activities → Мої), cursor-paged like `/v1/events`. */
export const meEventsQuerySchema = z.object({
	tab: z.enum(ME_EVENT_TABS),
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(50).default(20),
});

/** `GET /v1/events/map` (≤ 200 pins inside the bbox). */
export const eventsMapQuerySchema = z.object({ ...filters, bbox: bboxSchema });

/** `GET /v1/events/status`: why pins left the map (T-1333). */
export const EVENT_STATUS_IDS_MAX = 50;
export const eventStatusQuerySchema = z.object({
	ids: z
		.string()
		.transform((value) => value.split(','))
		.pipe(z.array(z.uuid()).min(1).max(EVENT_STATUS_IDS_MAX)),
});

export const eventIdParamsSchema = z.object({ id: z.uuid() });

const point = z.object({ lat: z.number(), lng: z.number() });

/**
 * Where to draw the event. `cell`: center of the ≈1 km cell, draw a 750 m circle (personal
 * events). `exact`: the real spot, draw a pin (venue events, or viewers allowed to see it).
 */
const placementSchema = z.object({ point, precision: z.enum(['cell', 'exact']) });

const userSummarySchema = z.object({
	id: z.uuid(),
	/** Null: the account was deleted (T-804); clients show a neutral placeholder. */
	displayName: z.string().nullable(),
	photoUrl: z.url().nullable(),
});

const seatsSchema = z.object({
	/** Includes the organizer (D17). */
	taken: z.number().int(),
	max: z.number().int(),
});

/** The viewer's own tie to a pin (T-1331); null for everyone else's. Never about other people. */
export const pinRelationSchema = z.enum(['organizer', 'accepted', 'pending']);

export const eventPinSchema = z.object({
	id: z.uuid(),
	category: z.string(),
	/** Public title; the map draws it under the marker (T-306b). */
	title: z.string(),
	placement: placementSchema,
	startAt: z.iso.datetime(),
	/** The pin leaves the map by itself when this passes (T-1333). */
	endAt: z.iso.datetime(),
	seats: seatsSchema,
	coverUrl: z.url().nullable(),
	/** Organizer's pin emoji (T-306f); null = category icon. */
	markerEmoji: z.string().nullable(),
	/** Venue events only: the venue account's photo as the pin. Always null for personal events (no faces on the map). */
	logoUrl: z.url().nullable(),
	relation: pinRelationSchema.nullable(),
});

export const eventCardSchema = z.object({
	id: z.uuid(),
	type: z.enum(['personal', 'venue']),
	category: z.string(),
	title: z.string(),
	startAt: z.iso.datetime(),
	endAt: z.iso.datetime(),
	placement: placementSchema,
	/** Nearest metro to the cell center; clients format «Біля м. X» (D31). Null if none ≤ 2 km. */
	landmark: z.object({ name: localizedNameSchema }).nullable(),
	seats: seatsSchema,
	acceptMode: z.enum(['auto', 'manual']),
	recruitmentOpen: z.boolean(),
	status: z.enum(['active', 'cancelled', 'completed']),
	coverUrl: z.url().nullable(),
	organizer: userSummarySchema,
	/** Organizer's rating as an organizer (T-905, D45); null below 3 reviews. */
	organizerRating: ratingSummarySchema,
	/** This event's own average (participants' reviews of the organizer); null below 3, venues. */
	rating: ratingSummarySchema,
});

const viewerSchema = z.object({
	/** The viewer's own application (for withdraw / leave), null if they never applied. */
	applicationId: z.uuid().nullable(),
	applicationStatus: z.enum(['pending', 'accepted', 'rejected', 'withdrawn', 'removed']).nullable(),
	isSaved: z.boolean(),
});

const publicDetailFields = {
	...eventCardSchema.shape,
	description: z.string(),
	extraConditions: z.string().nullable(),
	allowLateJoin: z.boolean(),
	minAge: z.number().int().nullable(),
	/** 17 for teens-only events (D44); null = no upper limit. */
	maxAge: z.number().int().nullable(),
	viewer: viewerSchema,
};

/** Exact meeting place: only for accepted participants and the organizer (PRIVACY §2). */
const exactPlaceSchema = z.object({
	point,
	placeName: z.string().nullable(),
	placeDetails: z.string().nullable(),
});

const participantDetailFields = {
	...publicDetailFields,
	exactPlace: exactPlaceSchema,
	participants: z.array(userSummarySchema),
	pinnedInfo: z.string().nullable(),
};

export const eventDetailSchema = z.discriminatedUnion('access', [
	z.object({ access: z.literal('public'), ...publicDetailFields }),
	z.object({ access: z.literal('participant'), ...participantDetailFields }),
	z.object({
		access: z.literal('organizer'),
		...participantDetailFields,
		pendingApplications: z.number().int(),
		/** For the edit form (T-306f). */
		markerEmoji: z.string().nullable(),
	}),
]);

export const eventsListResponseSchema = z.object({
	items: z.array(eventCardSchema),
	nextCursor: z.string().nullable(),
});

/** `gone`: missing or not visible to the viewer, deliberately without a reason. */
export const eventStatusSchema = z.enum(['active', 'cancelled', 'finished', 'gone']);

export const eventStatusResponseSchema = z.object({
	statuses: z.array(z.object({ id: z.uuid(), status: eventStatusSchema })),
});

export const eventsMapResponseSchema = z.object({
	pins: z.array(eventPinSchema),
	/** True when more than the pin budget matched; the client should zoom in. */
	truncated: z.boolean(),
});

/**
 * `PATCH /v1/events/:id` response: the organizer view plus how many pending applications the
 * manual → auto switch accepted (D21), so the UI can say so.
 */
export const updateEventResponseSchema = z.object({
	event: eventDetailSchema,
	autoAccepted: z.number().int(),
});

/** `PUT` / `DELETE /v1/events/:id/save` (bookmark; no profile gate). */
export const saveEventResponseSchema = z.object({ saved: z.boolean() });

export type EventsListQuery = z.infer<typeof eventsListQuerySchema>;
export type MeEventsQuery = z.infer<typeof meEventsQuerySchema>;
export type PinRelation = z.infer<typeof pinRelationSchema>;
export type EventStatus = z.infer<typeof eventStatusSchema>;
export type EventStatusQuery = z.infer<typeof eventStatusQuerySchema>;
export type EventStatusResponse = z.infer<typeof eventStatusResponseSchema>;
export type EventsMapQuery = z.infer<typeof eventsMapQuerySchema>;
export type EventPin = z.infer<typeof eventPinSchema>;
export type EventCard = z.infer<typeof eventCardSchema>;
export type EventDetail = z.infer<typeof eventDetailSchema>;
export type SaveEventResponse = z.infer<typeof saveEventResponseSchema>;
export type UpdateEventResponse = z.infer<typeof updateEventResponseSchema>;
export type EventsListResponse = z.infer<typeof eventsListResponseSchema>;
export type EventsMapResponse = z.infer<typeof eventsMapResponseSchema>;
export type UserSummary = z.infer<typeof userSummarySchema>;
